/** PIN Customers page - dedicated view for PIN-based authentication customers. */
import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Settings as GenerateIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AccountTable, { Account, CommonColumns } from "../../components/accounts/AccountTable";
import { ColumnDef } from "@tanstack/react-table";
import apiClient from "../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";

/** Extended account interface with PIN-specific fields. */
interface PINAccount extends Account {
  pin?: string;
  assigned_to?: string;
}

/** Breadcrumb component. */
function PINBreadcrumbs() {
  const navigate = useNavigate();

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
      <Link
        underline="hover"
        color="inherit"
        href="/"
        onClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
      >
        Home
      </Link>
      <Link
        underline="hover"
        color="inherit"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate("/accounts");
        }}
      >
        Customers
      </Link>
      <Typography color="text.primary">PIN Customers</Typography>
    </Breadcrumbs>
  );
}

/** PIN customer creation/edit dialog. */
interface AddPINDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editCustomer?: PINAccount | null;
}

function AddPINDialog({ open, onClose, onSave, editCustomer }: AddPINDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    assigned_to: "",
    initial_balance: "0",
  });

  // Populate form when editing
  useEffect(() => {
    if (editCustomer) {
      setFormData({
        name: editCustomer.account || "",
        assigned_to: editCustomer.assigned_to || "",
        initial_balance: editCustomer.balance?.toString() || "0",
      });
    } else {
      setFormData({ name: "", assigned_to: "", initial_balance: "0" });
    }
  }, [editCustomer, open]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = () => {
    if (!formData.name) return;

    if (editCustomer) {
      // Editing existing customer - just update balance
      onSave({
        id: editCustomer.id,
        balance: parseFloat(formData.initial_balance) || 0,
      });
    } else {
      // Creating new customer - generate PIN
      const pin = Math.floor(Math.random() * 90000000) + 10000000;

      onSave({
        tenant: "b1", // Default tenant
        account: `pin_${pin}`, // Account name based on PIN
        account_type: "*prepaid", // PIN customers are typically prepaid
        customer_type: "pin_test",
        balance: parseFloat(formData.initial_balance) || 0,
        pin: pin.toString(), // Store PIN for reference
        assigned_to: formData.assigned_to || null,
      });
    }

    setFormData({ name: "", assigned_to: "", initial_balance: "0" });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editCustomer ? "Edit PIN Customer" : "Add PIN Customer"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {editCustomer && (editCustomer as any).pin && (
            <Alert severity="success" sx={{ mt: 1 }}>
              <strong>PIN Code: {editCustomer.account}</strong>
              <br />
              PIN: {(editCustomer as any).pin}
            </Alert>
          )}
          <TextField
            label="Customer Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange("name")}
            placeholder="e.g., John Smith"
            disabled={!!editCustomer}
          />
          <TextField
            label="Assign to Reseller"
            fullWidth
            select
            value={formData.assigned_to}
            onChange={handleChange("assigned_to")}
            placeholder="Optional"
          >
            <MenuItem value="">None (Direct Customer)</MenuItem>
            <MenuItem value="reseller1">Reseller 1</MenuItem>
            <MenuItem value="reseller2">Reseller 2</MenuItem>
            <MenuItem value="reseller3">Reseller 3</MenuItem>
          </TextField>
          <TextField
            label="Initial Balance"
            fullWidth
            type="number"
            value={formData.initial_balance}
            onChange={handleChange("initial_balance")}
            placeholder="e.g., 50.00"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          {!editCustomer && (
            <Alert severity="info" sx={{ mt: 1 }}>
              PIN will be automatically generated (8 digits) when you click "Add Customer".
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name}
        >
          {editCustomer ? "Save Changes" : "Add Customer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Bulk PIN generation dialog. */
interface GeneratePINsDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (count: number, assignTo: string) => void;
}

function GeneratePINsDialog({ open, onClose, onGenerate }: GeneratePINsDialogProps) {
  const [count, setCount] = useState("10");
  const [assignTo, setAssignTo] = useState("");

  const handleGenerate = () => {
    const num = parseInt(count);
    if (num > 0 && num <= 1000) {
      onGenerate(num, assignTo);
      setCount("10");
      setAssignTo("");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate PIN Codes</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Number of PINs"
            fullWidth
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{ min: 1, max: 1000 }}
            helperText="Maximum 1000 PINs at once"
          />
          <TextField
            label="Assign to Reseller"
            fullWidth
            select
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
          >
            <MenuItem value="">None (Direct Customers)</MenuItem>
            <MenuItem value="reseller1">Reseller 1</MenuItem>
            <MenuItem value="reseller2">Reseller 2</MenuItem>
            <MenuItem value="reseller3">Reseller 3</MenuItem>
          </TextField>
          <Alert severity="info" sx={{ mt: 1 }}>
            This will create {count} new PIN customer accounts with auto-generated PINs.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleGenerate} variant="contained">
          Generate PINs
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** PIN Customers page. */
export default function PINCustomers() {
  const [customers, setCustomers] = useState<PINAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<PINAccount | null>(null);
  const navigate = useNavigate();

  /** Fetch PIN customers from API. */
  const fetchCustomers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/v1/accounts/?type=pin_test");
      setCustomers(response.data.accounts || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load PIN customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  /** Mask PIN for display (show only last 4 digits). */
  const maskPIN = (pin: string) => {
    if (!pin || pin.length < 4) return "****";
    return `****${pin.slice(-4)}`;
  };

  /** Table columns for PIN customers. */
  const columns = useMemo(() => {
    const cols: ColumnDef<PINAccount, any>[] = [
      CommonColumns.accountName,
      {
        accessorKey: "pin",
        header: "PIN Code",
        cell: (info) => {
          const pin = info.getValue() as string;
          return (
            <Chip
              label={maskPIN(pin || "")}
              size="small"
              variant="filled"
              sx={{ fontFamily: "monospace", fontSize: "1rem" }}
            />
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "assigned_to",
        header: "Assigned To",
        cell: (info) => {
          const assigned = info.getValue() as string;
          return assigned ? (
            <Chip label={assigned} size="small" color="primary" variant="outlined" />
          ) : (
            <Chip label="Direct" size="small" color="default" variant="outlined" />
          );
        },
        enableSorting: true,
      },
      CommonColumns.balance,
      CommonColumns.accountType,
      CommonColumns.status,
    ];
    return cols;
  }, []);

  /** Handle edit customer. */
  const handleEdit = (customer: PINAccount) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  /** Handle delete request — confirmed via styled dialog (UX review C2). */
  const [pendingDelete, setPendingDelete] = useState<PINAccount | null>(null);

  const handleDelete = (customerId: string) => {
    setPendingDelete(customers.find((c) => c.id === customerId) || null);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    try {
      await apiClient.delete(`/api/v1/accounts/${pendingDelete.id}`);
      setPendingDelete(null);
      await fetchCustomers();
    } catch (err: any) {
      setPendingDelete(null);
      setError(err.response?.data?.detail || "Failed to delete customer");
    }
  };

  /** Handle save customer (create or update). */
  const handleSave = async (data: any) => {
    try {
      if (selectedCustomer) {
        // For editing, we update the balance via the balance endpoint
        if (data.balance !== undefined) {
          await apiClient.post(`/api/v1/accounts/${selectedCustomer.id}/balance`, {
            amount: data.balance - (selectedCustomer.balance || 0),
            reason: "Admin adjustment"
          });
        }
      } else {
        await apiClient.post("/api/v1/accounts", data);
      }
      setDialogOpen(false);
      setSelectedCustomer(null);
      await fetchCustomers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save customer");
    }
  };

  /** Handle bulk PIN generation. */
  const handleGeneratePINs = async (count: number, assignTo: string) => {
    try {
      // Generate multiple PIN accounts
      const promises = Array.from({ length: count }, () => {
        const pin = Math.floor(Math.random() * 90000000) + 10000000;
        return apiClient.post("/api/v1/accounts", {
          tenant: "b1",
          account: `pin_${pin}`,
          account_type: "*prepaid",
          customer_type: "pin_test",
          balance: 0,
          pin: pin.toString(),
          assigned_to: assignTo || null,
        });
      });

      await Promise.all(promises);
      setGenerateDialogOpen(false);
      await fetchCustomers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to generate PINs");
    }
  };

  /** Type-specific actions for PIN customers. */
  const typeSpecificActions = (customer: PINAccount) => (
    <>
      {/* Add PIN-specific actions here like Regenerate PIN */}
    </>
  );

  /** Handle view CDRs. */
  const handleViewCDRs = (customer: PINAccount) => {
    navigate(`/cdrs?account=${customer.account}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PINBreadcrumbs />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">PIN Customers</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<GenerateIcon />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Generate PINs
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedCustomer(null);
              setDialogOpen(true);
            }}
          >
            Add PIN Customer
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {customers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No PIN customers found. Click "Add PIN Customer" to create your first PIN
            customer, or "Generate PINs" to create multiple PIN accounts in bulk.
          </Typography>
        </Paper>
      ) : (
        <AccountTable
          accounts={customers}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          typeSpecificActions={typeSpecificActions}
          onViewPIN={handleEdit}
        />
      )}

      {/* Add/Edit PIN customer dialog */}
      <AddPINDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedCustomer(null);
        }}
        onSave={handleSave}
        editCustomer={selectedCustomer}
      />

      {/* Generate PINs dialog */}
      <GeneratePINsDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onGenerate={handleGeneratePINs}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete PIN customer"
        message={`Delete ${pendingDelete?.account ?? "this customer"}?`}
        details="The account is disabled in the billing engine. Call history is retained for auditing."
        confirmLabel="Delete"
        severity="error"
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirmed}
      />
    </Box>
  );
}
